import React, { useContext, useState, useEffect, useRef, useCallback } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { FaBookmark, FaShoppingCart, FaCommentDots, FaTimes, FaBars } from 'react-icons/fa';
import Logo from '../../images/pngs/Logo.png';
import { LoginContext } from '../../Contexts/LoginContext';
import { useBookmarkContext } from '../../Contexts/BookmarkProvider';
import { useCart } from '../../Contexts/CartContext';
import ProfileDrawer from '../../Components/buyer/ProfileDrawer';
import CartDrawer from '../../Components/buyer/CartDrawer';
import NotificationPopup from './Notifications';
import LocationCurrencyModal, { getFlagUrl } from '../utils/LocationCurrencyModal';
import { PiUserSwitchFill } from 'react-icons/pi';
import { FaShop } from 'react-icons/fa6';
import { formatCurrency } from '../../data/constants';
import axios from 'axios';
import io from 'socket.io-client';

export default function BuyerHeader() {
  const { isLogin, isTablet, user, logout, currency, exchangeRate } = useContext(LoginContext);
  const headerLinks = isLogin
    ? ['home', 'categories', 'Products & Services', 'B2B', 'post Requirements', 'orders', 'blogs']
    : ['home', 'categories', 'Products & Services', 'B2B', 'blogs', 'contact'];

  const { cartItems } = useCart();
  const { bookmarkedSellers } = useBookmarkContext();
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [buyerOrderUnread, setBuyerOrderUnread] = useState(0);
  const [sellerOrderUnread, setSellerOrderUnread] = useState(0);
  const [buyerCredits, setBuyerCredits] = useState(null);
  const socketRef = useRef(null);
  const location = useLocation();

  const fetchUnread = useCallback(async () => {
    if (!isLogin || !user?._id) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/chats/userChats/${user._id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadMessages(res.data.totalUnread || 0);
    } catch (err) { /* silent */ }
  }, [isLogin, user?._id]);

  const fetchOrderUnread = useCallback(async () => {
    if (!isLogin || !user?._id) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/orders/notifications/counts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBuyerOrderUnread(res.data.buyerUnread || 0);
      setSellerOrderUnread(res.data.sellerUnread || 0);
    } catch (err) { /* silent */ }
  }, [isLogin, user?._id]);

  const fetchBuyerCredits = useCallback(async () => {
    if (!isLogin || !user?._id) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/payments/buyer/balance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBuyerCredits(res.data?.balance ?? 0);
    } catch (err) { setBuyerCredits(null); }
  }, [isLogin, user?._id]);

  useEffect(() => {
    fetchUnread();
    fetchOrderUnread();
    fetchBuyerCredits();
    const interval = setInterval(() => { fetchUnread(); fetchOrderUnread(); fetchBuyerCredits(); }, 15000);
    return () => clearInterval(interval);
  }, [fetchUnread, fetchOrderUnread, fetchBuyerCredits]);

  useEffect(() => { fetchUnread(); fetchOrderUnread(); fetchBuyerCredits(); }, [location.pathname, fetchUnread, fetchOrderUnread, fetchBuyerCredits]);

  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') { fetchUnread(); fetchOrderUnread(); fetchBuyerCredits(); } };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [fetchUnread, fetchOrderUnread, fetchBuyerCredits]);

  useEffect(() => {
    if (!isLogin || !user?._id) return;
    const socket = io(process.env.REACT_APP_Backend_Host_Name, { auth: { token: localStorage.getItem('token') } });
    socketRef.current = socket;
    socket.on('orderUnreadRefresh', () => fetchOrderUnread());
    socket.on('notificationRefresh', () => fetchUnread());
    socket.on('receiveMessage', () => fetchUnread());
    return () => socket.disconnect();
  }, [isLogin, user?._id, fetchOrderUnread, fetchUnread]);

  const userCountryCode = user?.buyerAddress?.country?.code || '';

  if (isMobileMenuOpen) {
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = 'unset';
  }

  const toggleCartDrawer = () => {
    setIsCartOpen(!isCartOpen);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Component for Navigation Links
  const NavigationLinks = ({ className, onClick }) => (
    <ul className={className}>
      {headerLinks.map((link) => (
        <li key={link}>
          {link === 'orders' ? (
            <NavLink
              to="/orders"
              className={({ isActive }) => (isActive ? 'active' : '')}
              onClick={onClick}
            >
              Orders
              {buyerOrderUnread > 0 && <span className="headerOrderUnreadBadge">{buyerOrderUnread}</span>}
            </NavLink>
          ) : (
            <NavLink
              to={link === 'home' ? '/' : link === "Products & Services" ? '/products-services' : link === 'B2B' ? '/b2b' : `/${link.replace(/\s+/g, '')}`}
              className={({ isActive }) => {
                if (link === 'B2B') {
                  return location.pathname.startsWith('/b2b') ? 'active' : '';
                }
                return isActive ? 'active' : '';
              }}
              end={link === 'home'}
              onClick={onClick}
            >
              {link.charAt(0).toUpperCase() + link.slice(1)}
            </NavLink>
          )}
        </li>
      ))}
    </ul>
  );

  // Component for Auth Buttons
  const AuthButtons = ({ className }) => {
    return isLogin !== null && <div className={className}>
      <Link to="/signin" className="signin">Sign In</Link>
      <Link to="/signup" className="signup">Sign Up</Link>
    </div>
  };

  // Component for Mobile Icons (Bookmark and Cart only)
  const MobileIconsSection = ({ className, onClick }) => (
    <div className={className}>
      <Link to="/account/credits" className="mobileBalanceLink" onClick={onClick} style={{ display: 'block', marginBottom: '8px' }}>
        {buyerCredits != null && buyerCredits > 0 ? `Credits: ${formatCurrency(buyerCredits, currency || 'USD', exchangeRate ?? 1)}` : 'Account credits'}
      </Link>
      <NavLink to="/bookmarkSellers" className="cartIconButton" onClick={onClick}>
        <FaBookmark /> Bookmarks
        {bookmarkedSellers.length > 0 && (
          <span className="cartItemCount">{bookmarkedSellers.length}</span>
        )}
      </NavLink>
      <button onClick={() => { toggleCartDrawer(); onClick && onClick(); }} className="cartIconButton">
        <FaShoppingCart /> Cart
        {cartItems.length > 0 && (
          <span className="cartItemCount">{cartItems.length}</span>
        )}
      </button>
      <Link
        to={"/settings"}
        state={{ isSellerAccount: false }}
      >
        Settings
      </Link>
      <Link to={`${"/contact"}`} onClick={() => setIsMobileMenuOpen(false)}>Contact Us</Link>
      <Link to={`${"/myReviews"}`}>My reviews</Link>
      <Link onClick={() => { logout() }}>Logout</Link>

      {(user?.role === "seller" && user?.sellerId) ? <Link to={'/sellerDashboard'} className="switchToSelling">
        <PiUserSwitchFill className='icon' />
        {'Switch to Selling'}
        {sellerOrderUnread > 0 && <span className="switchSellerUnreadBadge">{sellerOrderUnread}</span>}
      </Link> : <Link to={'/becomeSeller'} className="switchToSelling">
        <FaShop className='icon' />
        Become a Seller
      </Link>}
    </div>
  );


  return (
    <header className="buyerHeader" style={isTablet && isMobileMenuOpen ? { position: "fixed", width: "100%", top: "0", zIndex: "100" } : {}}>
      <div className="buyerHeaderLogo">
        <img src={Logo} alt="Chocow" />
      </div>

      <NavigationLinks className="buyerHeaderLinks" />

      <button
        className="mobileMenuButton"
        onClick={toggleMobileMenu}
        aria-label="Toggle mobile menu"
      >
        {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
      </button>

      {isLogin ? (
        <div className="buyerHeaderIcons">
          <button className="headerCountryBtn" onClick={() => setIsLocationModalOpen(true)}>
            {getFlagUrl(userCountryCode) ? (
              <img src={getFlagUrl(userCountryCode)} alt={userCountryCode} className="flagImg" />
            ) : (
              <span className="flagEmoji">🌍</span>
            )}
            {userCountryCode && <span className="countryCode">{userCountryCode}</span>}
          </button>
          <NavLink to="/bookmarkSellers" className="cartIconButton">
            <FaBookmark size={22} />
            {bookmarkedSellers.length > 0 && (
              <span className="cartItemCount">{bookmarkedSellers.length}</span>
            )}
          </NavLink>
          <button onClick={toggleCartDrawer} className="cartIconButton">
            <FaShoppingCart size={22} />
            {cartItems.length > 0 && (
              <span className="cartItemCount">{cartItems.length}</span>
            )}
          </button>
          <NavLink to="/chats" className="cartIconButton">
            <FaCommentDots size={22} />
            {unreadMessages > 0 && (
              <span className="cartItemCount">{unreadMessages}</span>
            )}
          </NavLink>
          {!isTablet && <> <NotificationPopup />
            {!isTablet && isLogin && buyerCredits != null && buyerCredits > 0 && (
              <Link to="/account/credits" className="headerBalanceBadge" title="Account credits (refunds from cancelled orders)">
                {formatCurrency(buyerCredits, currency || 'USD', exchangeRate ?? 1)}
              </Link>
            )}
            <ProfileDrawer sellerOrderUnread={sellerOrderUnread} /></>}
        </div>
      ) : (
        <AuthButtons className="buyerHeaderAuth" />
      )}

      <div className={`mobileNav ${isMobileMenuOpen ? 'active' : ''}`}>
        <NavigationLinks
          className="mobileNavLinks"
          onClick={() => setIsMobileMenuOpen(false)}
        />

        {isLogin ? (
          <MobileIconsSection
            className="mobileNavIcons"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        ) : (
          <AuthButtons className="mobileNavAuth" />
        )}
      </div>

      <CartDrawer
        isOpen={isCartOpen}
        toggleDrawer={toggleCartDrawer}
      />
      <LocationCurrencyModal
        isOpen={isLocationModalOpen}
        onClose={() => setIsLocationModalOpen(false)}
      />
    </header>
  );
}